import React, { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { useLayoutContext } from '../../../Context/Layout.context';
import { Grid, Typography } from '@material-ui/core';
import { getSmsNotificationSetups, updateSmsNotificationSetups } from '../../../services/admin/settings/systemSettings.service';
import { useStyles } from '../SystemSettings.styles';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { CancelButton } from '../../buttons/CancelButton.component';
import { ConfirmButton } from '../../buttons/ConfirmButton.component';
import { useTranslation } from 'react-i18next';
import { SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import SwitchOne from '../../SwitchOne/SwitchOne.component';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { ExaminersType, getExaminerTypeLabel } from '../../../models/examiners.types';

const columns: SelectableTableColumn[] = [
  {
    id: 'name',
    type: 'text',
    title: 'Nazwa',
    filterType: null,
  },
  {
    id: 'isActiveElement',
    type: 'children',
    title: 'Status możliwości wysyłki SMS przez role',
    filterType: null,
  },
];

export interface SmsNotificationSetupViewItem extends SmsNotificationSetupItem {
  [key: string]: Element | ReactNode | Date;
  isActiveElement: JSX.Element,
  name: string,
  modified?: boolean,
}

export interface SmsNotificationSetupItem  {
  id: string,
  examinerFunctionId: ExaminersType,
  allowSendSmsNotification: boolean
}

export interface ModifiedSmsNotificationItem {
  id: string,
  allowSendSmsNotification: boolean
}



export const SmsNotificationSetupManager: React.FC<{
  onClose: () => void;
  onSave: ()=> void;
}> = ({ onClose, onSave }) => {
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const classes = useStyles();
  const { t } = useTranslation();
  const { showLoader, openAlert } = useLayoutContext();
  const [smsNotificationSetupItems, setSmsNotificationSetupItems] = useState<SmsNotificationSetupViewItem[]>([]);


  const changedSmsNotificationSetupState = (
    checked: boolean,
    id: string,
    setStateFunc: Dispatch<SetStateAction<SmsNotificationSetupViewItem[]>>,
  ) => {
    setStateFunc((existingNotificationState) => {
      return existingNotificationState.map((item: SmsNotificationSetupViewItem) => {
        return item.id === id
          ? {
            ...item,
            isActiveElement: (
              <div
                style={{ display: 'flex', justifyContent: 'right' }}>
                <TooltipOne
                  title={`Wysyłka SMS przez role jest ${item.modified
                    ? (!item.allowSendSmsNotification ? 'wyłączona' : 'włączona')
                    : (item.allowSendSmsNotification ? 'wyłączona' : 'włączona')}`}
                  placement="top">
                  <SwitchOne
                    disableRipple
                    color={'primary'}
                    checked={checked}
                    onChange={(event, checked: boolean) =>
                      changedSmsNotificationSetupState(checked, item.id, setStateFunc)
                    }
                    inputProps={{ 'aria-label': 'ysyłka SMS przez role' }}
                  />
                </TooltipOne>
              </div>
            ),
            modified: !item.modified,
          }
          : item;
      });
    });
  };

  const buildTableItems = (data: SmsNotificationSetupItem[], setStateFunc: Dispatch<SetStateAction<SmsNotificationSetupViewItem[]>>) => {
    const tableItems: SmsNotificationSetupViewItem[] = [];
    data.forEach((item: SmsNotificationSetupItem) => {
      tableItems.push({
        id: item.id,
        name: getExaminerTypeLabel(item.examinerFunctionId),
        examinerFunctionId: item.examinerFunctionId,
        allowSendSmsNotification: item.allowSendSmsNotification,
        isActiveElement: (
          <div style={{ display: 'flex', justifyContent: 'right' }}>
            <TooltipOne
              title={`Wysyłka SMS przez role jest ${item.allowSendSmsNotification ? 'włączona' : 'wyłączona'}`}
              placement="top"
            >
              <SwitchOne
                disableRipple
                color={'primary'}
                checked={item.allowSendSmsNotification}
                onChange={(event, checked: boolean) => changedSmsNotificationSetupState(checked, item.id, setStateFunc)}
                inputProps={{ 'aria-label': 'Wysyłka SMS przez role' }}
              />
            </TooltipOne>
          </div>
        ),
      });
    });
    setStateFunc(tableItems);
  };

  const getSmsNotificationSetupDataAndBuildTableItems = () => {
    showLoader(true);
    getSmsNotificationSetups()
      .then(
        (result: SmsNotificationSetupItem[]) => {
          buildTableItems(result, setSmsNotificationSetupItems);
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => showLoader(false));
  };



  useEffect(() => {
    getSmsNotificationSetupDataAndBuildTableItems();
  }, []);

  useEffect(() => {
    if (smsNotificationSetupItems.find((item) => item.modified)) {
      setIsDataChanged(true);
    } else {
      setIsDataChanged(false);
    }
  }, [smsNotificationSetupItems]);

  const filterModifiedItemsAndTransform = (): ModifiedSmsNotificationItem[] => {
    return smsNotificationSetupItems.filter((item) => item.modified)
      .map((item) => ({ id: item.id, allowSendSmsNotification: !item.allowSendSmsNotification }));
  };


  const handleOnSave = () => {
    showLoader(true, 'Aktualizuję dane');
    updateSmsNotificationSetups(filterModifiedItemsAndTransform())
      .then(
        () => {
          openAlert('success');
          setIsDataChanged(false);
          setSmsNotificationSetupItems(smsNotificationSetupItems.map(item =>
            item.modified ? {
              ...item,
              allowSendSmsNotification: !item.allowSendSmsNotification,
              modified: false
            } : item 
          ));
          onSave();
        },
        () => {
          openAlert('save_error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };


  const handleOnClose = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onClose();
  };

  return (
    <>
      <Grid item xs={12}>
        <div className={classes.listHeader}>
          <div style={{ height: '100px' }}>
            <Typography className={classes.tabTitle}>{t('systemSettings:smsNotificationSetupManager.header')}</Typography>
            <Typography className={classes.tabSubTitle}>Określ czy poniższe role mogą wysłać powiadomienia SMS</Typography>
          </div>
        </div>
      </Grid>
      <Grid item xs={12} style={{ marginTop: '25px' }}>
        {smsNotificationSetupItems.length > 0 ? (
           <SelectableTable<SmsNotificationSetupViewItem>
             maxHeight={'200px'}
             columns={columns}
             withHeaderHidden
             rows={smsNotificationSetupItems}
             oddTableDoesNotChangeColor
           />
        ):null}
        
      </Grid>
      <Grid item xs={12} style={{ textAlign: 'right', alignItems: 'end', marginTop: 170 }}>
        <CancelButton onClick={handleOnClose}>Anuluj</CancelButton>
        <ConfirmButton onClick={handleOnSave} disabled={!isDataChanged}>
          Zapisz
        </ConfirmButton>
      </Grid>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onClose} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
